/* WeekplanEditor — leidsters bewerken thema/uitstapjes/menu voor hun groep(en).
 * Bedoeld voor de Groepsapp. Auto-load per groep + huidige-week (maandag). */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, Save, ChevronDown, ChevronUp, Copy } from "lucide-react";
import { toast } from "sonner";

const DAGEN = [
  { key: "ma", label: "Maandag" },
  { key: "di", label: "Dinsdag" },
  { key: "wo", label: "Woensdag" },
  { key: "do", label: "Donderdag" },
  { key: "vr", label: "Vrijdag" },
];

function mondayOf(iso) {
  const d = new Date(iso);
  const dow = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - dow);
  return d.toISOString().slice(0, 10);
}

export default function WeekplanEditor({ groups, activeGroupId }) {
  const [groupId, setGroupId] = useState(activeGroupId || (groups?.[0]?.id || ""));
  const [weekStart, setWeekStart] = useState(mondayOf(new Date().toISOString().slice(0, 10)));
  const [form, setForm] = useState({ thema: "", uitstapjes: "", menu: {} });
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => { setGroupId(activeGroupId || (groups?.[0]?.id || "")); }, [activeGroupId, groups]);

  useEffect(() => {
    if (!groupId) return;
    api.get("/weekplan", { params: { group_id: groupId, week_start: weekStart } })
      .then(r => setForm({
        thema: r.data.thema || "",
        uitstapjes: r.data.uitstapjes || "",
        menu: r.data.menu || {},
      }))
      .catch(() => setForm({ thema: "", uitstapjes: "", menu: {} }));
  }, [groupId, weekStart]);

  const setMenu = (dag, veld, value) => {
    setForm(f => ({ ...f, menu: { ...f.menu, [dag]: { ...(f.menu[dag] || {}), [veld]: value } } }));
  };

  const label = useMemo(() => {
    const s = new Date(weekStart); const e = new Date(weekStart); e.setDate(e.getDate() + 4);
    return `${s.toLocaleDateString("nl-NL", { day: "numeric", month: "short" })} – ${e.toLocaleDateString("nl-NL", { day: "numeric", month: "short" })}`;
  }, [weekStart]);

  const save = async () => {
    if (!groupId) { toast.error("Kies eerst een groep"); return; }
    setBusy(true);
    try {
      await api.post("/weekplan", { group_id: groupId, week_start: weekStart, thema: form.thema, uitstapjes: form.uitstapjes, menu: form.menu });
      toast.success("Weekplan opgeslagen");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Opslaan mislukt");
    } finally { setBusy(false); }
  };

  const copyToNext = async () => {
    if (!groupId) { toast.error("Kies eerst een groep"); return; }
    const next = new Date(weekStart);
    next.setDate(next.getDate() + 7);
    const nextIso = mondayOf(next.toISOString().slice(0, 10));
    setBusy(true);
    try {
      await api.post("/weekplan", { group_id: groupId, week_start: nextIso, thema: form.thema, uitstapjes: form.uitstapjes, menu: form.menu });
      toast.success("Gekopieerd naar volgende week");
      setWeekStart(nextIso);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Kopiëren mislukt");
    } finally { setBusy(false); }
  };

  const shift = (delta) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + delta * 7);
    setWeekStart(mondayOf(d.toISOString().slice(0, 10)));
  };

  return (
    <Card className="p-4 bg-white shadow-none space-y-3" data-testid="weekplan-editor">
      <button
        onClick={() => setExpanded(x => !x)}
        className="flex items-center justify-between w-full"
        data-testid="weekplan-editor-toggle"
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md flex items-center justify-center bg-[color:var(--sage-100)] text-[color:var(--sage)]">
            <CalendarDays className="h-4 w-4" />
          </div>
          <div className="text-left">
            <div className="font-medium text-neutral-800 text-sm">Weekplan bewerken</div>
            <div className="text-[11px] text-neutral-500">Thema, uitstapjes, menu — {label}</div>
          </div>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-neutral-500" /> : <ChevronDown className="h-4 w-4 text-neutral-500" />}
      </button>

      {expanded && (
        <div className="space-y-3 pt-2 border-t border-neutral-100">
          <div className="grid grid-cols-2 gap-2 items-end">
            <div>
              <Label className="text-xs">Groep</Label>
              <Select value={groupId} onValueChange={setGroupId}>
                <SelectTrigger data-testid="weekplan-editor-group"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {(groups || []).map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => shift(-1)} data-testid="weekplan-editor-prev">←</Button>
              <div className="flex-1 text-center text-xs font-medium text-neutral-700 py-2">{label}</div>
              <Button variant="ghost" size="sm" onClick={() => shift(1)} data-testid="weekplan-editor-next">→</Button>
            </div>
          </div>

          <div>
            <Label className="text-xs">Thema van de week</Label>
            <Input
              value={form.thema}
              onChange={e => setForm({ ...form, thema: e.target.value })}
              placeholder="Bijv. Herfst en blaadjes"
              data-testid="weekplan-editor-thema"
            />
          </div>

          <div>
            <Label className="text-xs">Uitstapjes / activiteiten</Label>
            <Textarea
              rows={2}
              value={form.uitstapjes}
              onChange={e => setForm({ ...form, uitstapjes: e.target.value })}
              placeholder="Bijv. Dinsdag naar het park, donderdag voorleesochtend"
              data-testid="weekplan-editor-uitstapjes"
            />
          </div>

          <div>
            <Label className="text-xs">Menu per dag</Label>
            <div className="space-y-2 mt-1">
              {DAGEN.map(d => {
                const m = form.menu[d.key] || {};
                return (
                  <div key={d.key} className="bg-neutral-50 rounded p-2 space-y-1" data-testid={`weekplan-editor-day-${d.key}`}>
                    <div className="text-xs font-medium text-neutral-700">{d.label}</div>
                    <Input
                      value={m.ochtend || ""}
                      onChange={e => setMenu(d.key, "ochtend", e.target.value)}
                      placeholder="Ochtendhap"
                      className="h-8 text-xs"
                      data-testid={`weekplan-editor-${d.key}-ochtend`}
                    />
                    <Input
                      value={m.lunch || ""}
                      onChange={e => setMenu(d.key, "lunch", e.target.value)}
                      placeholder="Lunch"
                      className="h-8 text-xs"
                      data-testid={`weekplan-editor-${d.key}-lunch`}
                    />
                    <Input
                      value={m.middag || ""}
                      onChange={e => setMenu(d.key, "middag", e.target.value)}
                      placeholder="Middaghap"
                      className="h-8 text-xs"
                      data-testid={`weekplan-editor-${d.key}-middag`}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={save}
              disabled={busy}
              data-testid="weekplan-editor-save"
              style={{ background: "var(--sage)" }}
              className="text-white flex-1"
            >
              <Save className="h-4 w-4 mr-1" /> {busy ? "Opslaan…" : "Opslaan"}
            </Button>
            <Button
              variant="outline"
              onClick={copyToNext}
              disabled={busy}
              data-testid="weekplan-editor-copy"
              className="border-[color:var(--sage)] text-[color:var(--sage)] hover:bg-[color:var(--sage-100)] flex-1"
              title="Kopieer huidige inhoud naar volgende week"
            >
              <Copy className="h-4 w-4 mr-1" /> Kopieer naar volgende week
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
